<template>
  <div>
    <Head title="Create Photo" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/photos">Photos</Link>
      <span class="text-indigo-400 font-medium">/</span> Create
    </h1>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <div class="flex flex-wrap -mb-8 -mr-6 p-8">
        <input type="hidden" id="_token" name="_token" :value="csrf_token" />
        <select-input @change="onProjectChange($event)" class="pb-8 pr-6 w-full lg:w-1/2" label="Project">
          <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
        </select-input>
      </div>
      <div v-bind="getRootProps()" class="flex px-8 py-4  items-center justify-center w-full">
        <label for="dropzone-file" class="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
          <div class="flex flex-col items-center justify-center pt-5 pb-6">
            <svg class="w-10 h-10 mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
            <p class="mb-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-semibold">Click to upload</span> or drag and drop</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">PNG, JPG</p>
          </div>
          <input v-bind="getInputProps()" />
        </label>
      </div>
      <div class="flex flex-wrap -mb-8 -mr-6 p-8">
        <table class="w-full text-left text-sm font-light">
          <tbody>
            <tr
              v-for="file in acceptedFilesRef" :key="file.name"
              class="border-b transition duration-300 ease-in-out hover:bg-neutral-100 dark:border-neutral-500 dark:hover:bg-neutral-600">
              <td class="whitespace-nowrap px-1 py-4 font-medium">
                <svg v-if="uploadResultRef[file.name] === 2" class="flex-shrink-0 w-5 h-5 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>
                <svg v-if="uploadResultRef[file.name] === 0 || uploadResultRef[file.name] === 1" class="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>
                <svg v-if="uploadResultRef[file.name] === -1" class="w-4 h-4 mr-1.5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg>
              </td>
              <td class="whitespace-nowrap px-6 py-4 font-medium">
                <span>{{ file.name.length > 65 ? file.name.substring(0, 65) + '...' : file.name }}</span>
              </td>
              <td class="whitespace-nowrap px-6 py-4">
                <a v-if="uploadResultRef[file.name] === 0" class="font-medium ml-2 text-red-600 dark:text-blue-500 hover:underline" @click="removeFile(file.name)">Delete</a>
                <div v-if="uploadResultRef[file.name] === 1" class="px-3 py-1 text-xs font-medium leading-none text-center text-blue-800 bg-blue-200 rounded-full animate-pulse dark:bg-blue-900 dark:text-blue-200">uploading...</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
        <Link class="text-blue-600 hover:underline mr-2" tabindex="-1" href="/photos">Go back</Link>
        <loading-button
          :loading="form.processing"
          @click="upload"
          class="btn-indigo"
          type="button">Upload photos</loading-button>
      </div>
    </div>
  </div>
</template>
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInput from '@/Shared/SelectInput.vue'
import { useDropzone } from 'vue3-dropzone'
import { ref } from 'vue'
import axios from 'axios'

export default {
  components: {
    SelectInput,
    Head,
    Link,
    LoadingButton,
  },
  layout: Layout,
  remember: 'form',
  props: {
    projects: Array,
  },
  setup() {
    let acceptedFilesRef = ref([])
    let uploadResultRef = ref({})
    let projectIdRef = ref(0)

    const onProjectChange = (event) => {
      projectIdRef.value = event.target.value
    }

    const onDrop = (acceptFiles, rejectReasons) => {
      acceptedFilesRef.value = [...(acceptedFilesRef.value || []), ...acceptFiles]
      acceptedFilesRef.value.forEach(f => {
        uploadResultRef.value[f.name] = uploadResultRef.value[f.name] || 0
      })
    }
    const removeFile = (fileName) => {
      console.log(fileName, acceptedFilesRef.value)
      acceptedFilesRef.value = acceptedFilesRef.value.filter(file => file.name !== fileName)
      console.log(fileName, acceptedFilesRef.value)
    }

    const upload = () => {
      if (!projectIdRef.value) {
        alert('Select project')
        return
      }
      const queue = [ ...acceptedFilesRef.value ].reverse()
      if (queue.length === 0) {
        alert('Select files')
        return
      }
      const processQueue = (file) => {
        if (uploadResultRef.value[file.name] === 2) {
          processQueue(queue.pop())
          return
        }
        uploadResultRef.value[file.name] = 1
        const formData = new FormData()
        formData.set('project_id', projectIdRef.value)
        formData.set('photo', file)
        axios.post('/photos', formData, {
          withCredentials: true,
        }).then(() => {
          uploadResultRef.value[file.name] = 2
          if (queue.length > 0) {
            processQueue(queue.pop())
          }
        }).catch((error) => {
          uploadResultRef.value[file.name] = -1
        })
      }

      console.log('queue', queue)
      if (queue.length === 0) {
        return
      }
      processQueue(queue.pop())
    }

    const { getRootProps, getInputProps, ...rest } = useDropzone({ onDrop })

    return {
      getRootProps,
      getInputProps,
      acceptedFilesRef,
      uploadResultRef,
      projectIdRef,
      onProjectChange,
      removeFile,
      upload,
      ...rest,
    }
  },
  data() {
    return {
      files: [],
      form: this.$inertia.form({
        project_id: null,
      }),
    }
  },
  methods: {
    store() {
      this.form.post('/streams')
    },
  },
}
</script>
